/* ============================================================
 * FILE GENERATO — NON MODIFICARE A MANO
 *
 * Sorgente: backend/services/informativaPrivacy.js
 * Lo riscrive frontend/scripts/generaInformativa.js a ogni build.
 * Qualunque modifica fatta qui sparisce al build successivo:
 * si corregge l'originale nel backend.
 *
 * Esiste perche' Create React App non compila fuori da src/, e
 * l'informativa che il paziente LEGGE deve essere parola per parola
 * quella del PDF che FIRMA.
 * ============================================================ */

/**
 * informativaPrivacy.js — il testo dell'informativa, in un posto solo.
 *
 * PERCHE' ESISTE
 * Fino al 23 settembre 2026 l'informativa che il paziente LEGGE sullo
 * schermo e quella del PDF che FIRMA erano due copie scritte a mano in
 * due file diversi. Erano gia' divergenti: nel PDF c'era una finalita'
 * in piu' - «Difesa in giudizio di un diritto del Titolare» - che sullo
 * schermo non compariva. E il PDF si chiude con «dichiara di aver letto
 * e compreso l'informativa che precede», cioe' dichiara di aver letto
 * un testo che nessuno gli aveva mostrato per intero.
 *
 * Stessa storia per gli anni di conservazione delle fotografie: scritti
 * a mano nel frontend («non oltre 10 anni») e presi dai parametri nel
 * PDF. Oggi coincidono perche' il parametro non e' impostato e il
 * ripiego e' 10; su un'installazione di Coorte con un valore diverso il
 * paziente accetterebbe un numero e firmerebbe l'altro.
 *
 * IL TESTO DEL PDF E' IL RIFERIMENTO, non quello dello schermo: e' il
 * documento firmato e conservato, ed e' quello che varrebbe davanti a
 * chiunque. Quindi qui c'e' il testo del PDF, e lo schermo si adegua.
 *
 * I DOCUMENTI GIA' FIRMATI NON SI TOCCANO. Gli 87 moduli privacy dei
 * pazienti veri restano come sono; se la differenza sulla difesa in
 * giudizio vada sanata e come, lo dice il legale, non questo file.
 *
 * COME LO LEGGE IL FRONTEND
 * Create React App non compila fuori da `src/`, quindi una copia ci
 * vuole per forza. NON si tiene a mano: `frontend/scripts/generaInformativa.js`
 * la riscrive a ogni build, come gia' fa `generaDenaro.js`. Chi modifica
 * la copia se la vede riscritta; chi modifica questo file la vede
 * propagata. Generare invece di confrontare rende la divergenza
 * impossibile anziche' rilevabile - ed e' proprio la divergenza che
 * questo file esiste per chiudere.
 *
 * NIENTE `require` QUI DENTRO: il generatore copia il file cosi' com'e'.
 * I valori che cambiano - il titolare, gli anni - arrivano come
 * argomenti, e chi chiama se li procura da dove sa (il server dai
 * parametri, la schermata dalla rotta pubblica della struttura).
 */

/**
 * L'informativa, come elenco di sezioni.
 *
 * Ogni sezione: { titolo, paragrafi: [], elenco: [] }.
 * Una forma sola per tutti e due i lettori: il PDF la stampa con
 * `sezione`/`paragrafo`/`elenco`, la schermata con i suoi tag.
 *
 * @param {object} st  { titolareEsteso }
 */
export function informativaPrivacy(st) {
    const titolare = (st && st.titolareEsteso) || 'la struttura';
    return [
        {
            titolo: 'Titolare del trattamento',
            paragrafi: [
                `Titolare del trattamento e ${titolare}, contattabile ai recapiti indicati in intestazione.`,
            ],
            elenco: [],
        },
        {
            titolo: 'Categorie di dati trattati',
            paragrafi: [],
            elenco: [
                'Dati comuni: dati anagrafici, di residenza e di contatto, dati fiscali e di fatturazione.',
                'Categorie particolari di dati (art. 9 GDPR): dati relativi alla salute, anamnesi, diagnosi, '
                + 'trattamenti eseguiti, terapie prescritte, immagini cliniche.',
            ],
        },
        {
            titolo: 'Finalita e basi giuridiche',
            elenco: [
                'Erogazione delle prestazioni sanitarie, diagnosi, cura e gestione della cartella clinica '
                + 'informatizzata - art. 9, par. 2, lett. h) GDPR.',
                'Adempimenti fiscali, contabili e amministrativi, inclusa la trasmissione al Sistema Tessera '
                + 'Sanitaria - art. 6, par. 1, lett. c) GDPR, obbligo di legge.',
                'Difesa in giudizio di un diritto del Titolare - artt. 6, par. 1, lett. f) e 9, par. 2, lett. f) GDPR.',
                'Invio di promemoria e comunicazioni organizzative sugli appuntamenti - consenso dell interessato.',
                'Trasmissione di referti e documentazione sanitaria via email - consenso dell interessato.',
                'Comunicazione dello stato di salute a familiari o persona di fiducia - consenso dell interessato.',
                'Invio di comunicazioni promozionali e informazioni sui servizi - consenso dell interessato.',
            ],
            paragrafi: [
                'Il conferimento dei dati per le finalita di cura e per gli adempimenti di legge e necessario: il '
                + 'rifiuto rende impossibile erogare la prestazione. Il conferimento per le finalita soggette a consenso '
                + 'e facoltativo e il rifiuto non pregiudica in alcun modo l accesso alle cure.',
            ],
        },
        {
            titolo: 'Modalita del trattamento',
            paragrafi: [
                'I dati sono trattati con strumenti cartacei e informatici, mediante software gestionale sanitario '
                + 'ospitato su server situati nel territorio dell Unione Europea, con misure tecniche e organizzative '
                + 'adeguate ai sensi dell art. 32 GDPR: controllo degli accessi con credenziali individuali, cifratura '
                + 'delle comunicazioni, tracciamento delle operazioni, backup periodici.',
            ],
            elenco: [],
        },
        {
            titolo: 'Destinatari',
            paragrafi: [
                'I dati possono essere comunicati a: personale sanitario e amministrativo autorizzato; medici e '
                + 'professionisti collaboratori della struttura; laboratori di analisi e strutture sanitarie esterne '
                + 'coinvolte nel percorso di cura; consulente fiscale e contabile; fornitori di servizi informatici e di '
                + 'conservazione documentale, nominati Responsabili del trattamento ai sensi dell art. 28 GDPR; autorita '
                + 'pubbliche nei casi previsti dalla legge. Non e previsto alcun trasferimento verso Paesi terzi extra-UE. '
                + 'I dati non sono oggetto di processi decisionali automatizzati ne di profilazione.',
            ],
            elenco: [],
        },
        {
            titolo: 'Periodo di conservazione',
            paragrafi: [],
            elenco: [
                'Documentazione sanitaria e cartella clinica: conservata a tempo illimitato, quale atto indispensabile '
                + 'a garantire la certezza del diritto e la tutela della salute dell interessato.',
                'Documentazione fiscale e contabile: 10 anni dalla registrazione.',
                'Immagini fotografiche: secondo quanto indicato nel modulo di consenso dedicato.',
                'Dati trattati sulla base del consenso: fino a revoca del consenso.',
            ],
        },
        {
            titolo: 'Diritti dell interessato',
            paragrafi: [
                'L interessato puo esercitare in qualsiasi momento i diritti previsti dagli artt. 15-22 GDPR: accesso, '
                + 'rettifica, cancellazione, limitazione, portabilita, opposizione, nonche revoca del consenso prestato, '
                + 'senza che cio pregiudichi la liceita del trattamento effettuato prima della revoca. Le richieste vanno '
                + 'indirizzate ai recapiti del Titolare. E inoltre riconosciuto il diritto di proporre reclamo al Garante '
                + 'per la protezione dei dati personali (www.garanteprivacy.it).',
            ],
            elenco: [],
        },
    ];
}

/**
 * La frase sulla conservazione delle fotografie.
 *
 * Il numero di anni NON si scrive qui: arriva dal parametro
 * `foto_anni_conservazione`, che ogni installazione imposta con
 * `installa.js`. Scritto nel codice, sarebbe il numero di Rimedical
 * stampato sul consenso di un'altra clinica.
 *
 * @param {object} st  { anniConservazioneFoto }
 */
export function conservazioneFoto(st) {
    const anni = (st && st.anniConservazioneFoto) || 10;
    return 'Le immagini acquisite per la finalita A sono conservate insieme alla documentazione sanitaria e per il '
         + 'medesimo periodo. Le immagini utilizzate per le finalita B e C sono conservate fino a revoca del '
         + `consenso e comunque per non oltre ${anni} anni dall acquisizione.`;
}

